import { Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, finalize, map, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { Routes } from 'src/app/accountancy/utilities/classes/routes.class';
import { WaitHandler } from '../../../utilities/classes/wait-handler.class';
import { Pagination } from 'src/app/accountancy/utilities/classes/pagination.class';
import { LiquidationService } from './liquidation-service/liquidation.service';
import { ManageApproversService } from '../../account-management/manage-approvers/manage-approvers-service/manage-approvers.service';
import { LoginService } from '../../../authentication/login/login-service/login.service';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';
import { AccSimpleInputTextFormService } from '../../../custom-components/acc-simple-input-text-form/acc-simple-input-text-form-service/acc-simple-input-text-form.service';
import { MatDialog } from '@angular/material/dialog';
import { AccSearchTipComponent } from 'src/app/accountancy/custom-components/acc-search-tip/acc-search-tip.component';
import { AccTableComponent } from 'src/app/accountancy/custom-components/acc-table/acc-table.component';
import { FormGroup } from '@angular/forms';
import { LiquidationInterface } from 'src/app/accountancy/utilities/interfaces/liquidation.interface';

@Component({
  selector: 'app-liquidation',
  templateUrl: './liquidation.component.html',
  styleUrls: ['./liquidation.component.scss']
})
export class LiquidationComponent implements OnDestroy {

  public readonly routes = Routes;
  public readonly Helper = Helper;

  public readonly waitHandler = new WaitHandler();
  public readonly pagination = new Pagination();
  public readonly liqList$ = new BehaviorSubject<LiquidationInterface[]>([]);

  public readonly fgliqFilter = this._newFgliqFilter();

  private readonly _subsGetliqList = this._getSubsGetliqList();

  public readonly liqApprovalStatuses$ = this._getliqApprovalStatuses();

  constructor(
    private _liqS: LiquidationService,
    private _mApprovalS: ManageApproversService,
    private _loginS: LoginService,
    private _gApiS: GlobalApiService,
    private _simpleFormS: AccSimpleInputTextFormService,
    private _matDialog: MatDialog,
  ) { }

  public ngOnDestroy(): void {
    this._subsGetliqList.unsubscribe();
  }

  public readonly dialogs = {
    openSearch: () => this._matDialog.open(AccSearchTipComponent),
    openPrint: async (table: AccTableComponent) => table.printTable(),
  }

  private _getliqApprovalStatuses() {
    return of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._mApprovalS.getApprovalLevels("Liquidation").pipe(
        finalize(() => this.waitHandler.finishRequest()),
      ))
    )
  }

  private _newFgliqFilter() {
    return new FormGroup({
      Type: Helper.reactiveForms.control<string>('My Requests'),
      Status: Helper.reactiveForms.control<string>('All Status'),
    });
  }

  private _getSubsGetliqList() {
    return combineLatest([
      this.fgliqFilter.valueChanges.pipe(startWith(this.fgliqFilter.value)),
      this.pagination.searchQuery$.pipe(startWith(this.pagination.query), debounceTime(500), distinctUntilChanged()),
      this.pagination.pageLimit$,
      this.pagination.currentPage$,
      this.pagination.reloader$.pipe(startWith(null)),
    ]).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(([filter, query, limit]) => {

        const userId = this._loginS.userData?.ID;
        const requestFilter = {
          RequestedBy: filter?.Type && filter.Type === 'My Requests' ? userId : null,
          Approver: filter?.Type && filter.Type === 'For My Approval' ? userId : null,
          Status: filter?.Status && filter.Status === 'All Status' ? null : filter?.Status,
        };

        return this._liqS.getForLiquidation(this.pagination.start, limit, query, requestFilter).pipe(
          finalize(() => this.waitHandler.finishRequest()),
          map(values => values.body),
          tap(values => {
            this.pagination.setTotalCount(values?.total_rows);
            this.liqList$.next(values?.data ?? []);
          }),
        );
      })
    ).subscribe();
  }

}
